import { useEffect } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'ui/links';

import { NodeEditor } from 'modules/workflows/components/NodeEditor';
import { getIsNodeEditorReadOnly, updateNodeData } from 'modules/workflows/ducks';
import { Subtitle2 } from 'ui/theme';
import {
  ENDPOINT_FIELD,
  NODE_DATA_FIELDS,
  PERMIT_FIELD,
  RADIO_VAL,
  RESOURCE_FIELD,
  VALIDATION_MESSAGES,
  WEBHOOK_FIELD,
} from './CallWebhookEditor.constants';
import {
  Hr,
  Section,
  StyledBorderedRadioListField,
  StyledDescription,
} from './CallWebhookEditor.styled';
import {
  CallWebhookEditorFormSchemaType,
  CallWebhookFormProvider,
  CallWorkflowEditorProps,
} from './CallWebhookFormProvider';
import { EndpointInput } from './EndpointInput';
import { WebhookRateLimit } from './WebhookRateLimit';
import { WebhookSelect } from './WebhookSelect';

/**
 * Returns nodeData with unused values set to `undefined` to overwrite redux store values
 * @param values
 */
export const getUpdatedNodeData = (values: Record<string, any>) =>
  NODE_DATA_FIELDS.reduce((acc, field) => {
    let value = values?.nodeData?.[field];

    if (field === PERMIT_FIELD && value) value = Number(value);
    if (field === RESOURCE_FIELD && value) {
      value = {
        name: value,
      };
    }
    return { ...acc, [field]: value || undefined };
  }, {});

export const CALL_WEBHOOK_TEST_ID = 'call-webhook-editor';

const CallWebhookEditorCmp = ({
  nodeId,
  nodeData,
  clonedFromNodeData,
  ...rest
}: CallWorkflowEditorProps) => {
  const dispatch = useDispatch();
  const isReadOnly = useSelector(getIsNodeEditorReadOnly);

  // React hook form
  const { getValues, setValue } = useFormContext();
  const radioVal = useWatch({ name: RADIO_VAL });
  const { dirtyFields, errors = {} } = useFormState<CallWebhookEditorFormSchemaType>();
  const { nodeData: nodeDataErrors } = errors;
  const hasCloningError = nodeDataErrors?.[WEBHOOK_FIELD]?.message === VALIDATION_MESSAGES.CLONE;
  const hasRateLimitError = nodeDataErrors?.[PERMIT_FIELD];

  const handleUpdate = () => {
    const values = getValues();
    const updatedNodeData = getUpdatedNodeData(values);

    dispatch(updateNodeData(updatedNodeData, nodeId));
  };

  useEffect(() => {
    // unset resource name value if user inputs an endpoint
    if (radioVal === ENDPOINT_FIELD) {
      setValue(`nodeData.${RESOURCE_FIELD}`, null);
    }
  }, [radioVal]);

  return (
    <NodeEditor
      {...rest}
      canUpdate={!hasRateLimitError}
      dataTest={CALL_WEBHOOK_TEST_ID}
      getHasUnsavedChanges={() => !!dirtyFields.nodeData}
      isCloneWarningBannerShown={hasCloningError}
      onUpdate={handleUpdate}
    >
      <StyledDescription>
        Users will move on to the next step if the request succeeds. If the request fails, the user
        will exit the journey.{' '}
        <Link
          hasOpenNewTabIcon
          href="https://support.iterable.com/hc/en-us/articles/8120791290260#failed-webhook-calls"
          target="_blank"
          rel="noreferrer noopener"
        >
          Learn more.
        </Link>
      </StyledDescription>
      <Subtitle2>Call webhook</Subtitle2>
      <Section>
        <StyledBorderedRadioListField
          name={RADIO_VAL}
          isReadOnly={isReadOnly}
          options={[
            {
              name: 'Use existing webhook',
              value: WEBHOOK_FIELD,
              id: WEBHOOK_FIELD,
              ...(radioVal === WEBHOOK_FIELD && {
                description: (
                  <WebhookSelect
                    clonedFromNodeData={clonedFromNodeData}
                    hasCloningError={hasCloningError}
                    isReadOnly={isReadOnly}
                    nodeData={nodeData}
                  />
                ),
              }),
            },
            {
              name: 'Endpoint URL',
              value: ENDPOINT_FIELD,
              id: ENDPOINT_FIELD,
              ...(radioVal === ENDPOINT_FIELD && {
                description: <EndpointInput nodeData={nodeData} isReadOnly={isReadOnly} />,
              }),
            },
          ]}
        />
      </Section>
      <Hr />
      <Section>
        <WebhookRateLimit isReadOnly={isReadOnly} />
      </Section>
    </NodeEditor>
  );
};

export const CallWebhookEditor = (props: CallWorkflowEditorProps) => (
  <CallWebhookFormProvider {...props}>
    <CallWebhookEditorCmp {...props} />
  </CallWebhookFormProvider>
);
